import React, {
  Dispatch,
  FC,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  ButtonBox,
  GridTable,
  InputDatePicker,
  LoadingBox,
  PrivilegesChecker,
  SelectBox,
  TextBox,
} from "../..";
import {
  ActionTypeEnum,
  DailyTransactionTypeEnum,
  HasFormIdModel,
  InputDatePickerTimeDisplayModeEnum,
  LookupItemModel,
  LookupTypeEnum,
  RequestActionModel,
  SearchDailyTransactionTypeEnum,
  SearchTransactionRequestModel,
  TableActionColumnsStyleEnum,
  TransactionResponseModel,
} from "../../../models";
import { searchTransactions } from "../../../serviceBroker/transactionApiServiceBroker";
import {
  generateGuid,
  getLabelName,
  getPagePrivileges,
  isArabicCurrentLanguage,
} from "../../../utils";
import { getLookupByType } from "../../../serviceBroker/lookupApiServiceBroker";

interface TransactionListProps extends HasFormIdModel {
  transactionType: DailyTransactionTypeEnum;
  onActionEvent: (o: RequestActionModel) => void;
  setIsRefresh: Dispatch<SetStateAction<boolean>>;
  isRefresh: boolean;
}

export const TransactionList: FC<TransactionListProps> = ({
  transactionType,
  onActionEvent = () => {},
  setIsRefresh,
  isRefresh = false,
  formId,
}) => {
  //#region variables
  const isArabic = isArabicCurrentLanguage();
  const defaultPageSize: number = 50;
  const initialValues: SearchTransactionRequestModel = {
    pageNumber: 1,
    pageSize: defaultPageSize,
    transactionType: transactionType,
    searchType: SearchDailyTransactionTypeEnum.itemName,
    searchValue: "",
  };
  const searchTypeList: LookupItemModel[] = [
    {
      name: getLabelName("ItemName"),
      nameAr: getLabelName("ItemName"),
      value: SearchDailyTransactionTypeEnum.itemName.toString(),
    },
    {
      name: getLabelName("ItemCode"),
      nameAr: getLabelName("ItemCode"),
      value: SearchDailyTransactionTypeEnum.code.toString(),
    },
    // {
    //   name: getLabelName("Id"),
    //   nameAr: getLabelName("Id"),
    //   value: SearchDailyTransactionTypeEnum.id.toString(),
    // },
    // {
    //   name: getLabelName("Serial"),
    //   nameAr: getLabelName("Serial"),
    //   value: SearchDailyTransactionTypeEnum.serial.toString(),
    // },
    {
      name: getLabelName("barCode"),
      nameAr: getLabelName("barCode"),
      value: SearchDailyTransactionTypeEnum.barCode.toString(),
    },
    {
      name: getLabelName("Code"),
      nameAr: getLabelName("Code"),
      value: SearchDailyTransactionTypeEnum.transactionCode.toString(),
    },
  ];

  //#endregion
  //#region state

  const customerAccountsSelectBoxMultiselectRef = useRef<any>();
  const [data, setData] = useState<TransactionResponseModel[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [loading, setLoading] = useState(false);
  const [beneficiaryList, setBeneficiaryList] = useState<LookupItemModel[]>([]);
  const [cityList, setCityList] = useState<LookupItemModel[]>([]);
  const [searchRequest, setSearchRequest] =
    useState<SearchTransactionRequestModel>(initialValues);
  //#endregion
  //#region useEffect
  useEffect(() => {
    const fillData = async () => {
      setLoading(true);
      await getAllLookupsPromiseAll();
      //await getTransactions();
      setLoading(false);
    };
    fillData().then(() => {});
  }, []);
  useEffect(() => {
    const fillData = async () => {
      setLoading(true);
      await getTransactions();
      setLoading(false);
    };
    fillData().then(() => {});
  }, [
    searchRequest.pageNumber,
    searchRequest.pageSize,
    searchRequest.searchGuid,
  ]);
  useEffect(() => {
    ``;
    if (isRefresh) {
      const fillData = async () => {
        setLoading(true);
        await getTransactions();
        setLoading(false);
      };
      fillData().then(() => {});
      setIsRefresh(false);
    }
  }, [isRefresh]);
  //#endregion
  //#region functions
  const isTransactionApplicableForPayment = () => {
    const transactionTypes = [DailyTransactionTypeEnum.SalesInvoice];
    return transactionTypes.includes(transactionType);
  };
  const isTransactionApplicableForRefund = () => {
    const transactionTypes = [
      DailyTransactionTypeEnum.PurchasesInvoice,
      DailyTransactionTypeEnum.SalesInvoice,
      DailyTransactionTypeEnum.POSSales,
      DailyTransactionTypeEnum.POSRestaurant,
    ];
    return transactionTypes.includes(transactionType);
  };
  const isTransactionApplicableForIssueSalesRequest = () => {
    const transactionTypes = [
      DailyTransactionTypeEnum.Order,
      DailyTransactionTypeEnum.ShowPrice,
      DailyTransactionTypeEnum.CarRegistration,
    ];
    return transactionTypes.includes(transactionType);
  };
  const availableActions = [
    ActionTypeEnum.View,
    ActionTypeEnum.ModifyStart,
    ActionTypeEnum.DeleteStart,
    ActionTypeEnum.PrintPdf,
    //isTransactionApplicableForPayment() &&ActionTypeEnum.InitializeCustomerPayment,
    //isTransactionApplicableForRefund() &&   ActionTypeEnum.TransactionRefundIInitialization,
    //isTransactionApplicableForIssueSalesRequest() && ActionTypeEnum.IssueSaleTransactionFromSalePriceInitialization
  ].filter((action): action is ActionTypeEnum => Boolean(action));
  const actionResolver = (
    row: TransactionResponseModel,
    rowIndex: number
  ): ActionTypeEnum[] => {
    const availableActions: ActionTypeEnum[] = [];
    if (!row) return availableActions;
    const {
      isValidToUseAsTransaction,
      isValidForReturn,
      isValidToUseAsSource,
      isEnablePay
    } = row;
    if (isValidToUseAsTransaction || isValidToUseAsSource) {
      availableActions.push(
        ActionTypeEnum.IssueSaleTransactionFromSalePriceInitialization
      );
    }
    if (isValidForReturn) {
      availableActions.push(ActionTypeEnum.TransactionRefundIInitialization);
    }
    if (isEnablePay) {
      availableActions.push(ActionTypeEnum.InitializeCustomerPayment);
    }
    // Example: Add "View" for every odd row (for development or alternating UI behavior)
    // if (rowIndex % 2 !== 0) {
    //   availableActions.push(ActionTypeEnum.View);
    // }
    return availableActions;
  };
  const _isSalesComponent: boolean = [
    DailyTransactionTypeEnum.SalesInvoice,
    DailyTransactionTypeEnum.SalesRefund,
    DailyTransactionTypeEnum.ShowPrice,
    DailyTransactionTypeEnum.Order,
    DailyTransactionTypeEnum.POSSales,
    DailyTransactionTypeEnum.POSSalesRefund,
    DailyTransactionTypeEnum.POSRestaurant,
    DailyTransactionTypeEnum.Reserve_Restaurant_Bill,
    DailyTransactionTypeEnum.ReturnPOSRestaurant,
  ].includes(transactionType);
  const getAllLookupsPromiseAll = async () => {
    const [beneficiaryLookupList, cityListLookupList] = await Promise.all([
      _isSalesComponent
        ? getLookupByType(LookupTypeEnum.Customers, true, false)
        : getLookupByType(LookupTypeEnum.Supplier, true, false),
      getLookupByType(LookupTypeEnum.Cities, true, false),
    ]);
    setBeneficiaryList(beneficiaryLookupList);
    setCityList(cityListLookupList);
  };
  const getTransactions = async () => {
    if (searchRequest.searchValue != null && searchRequest.searchValue != "") {
      searchRequest.itemName =
        searchRequest.searchType == SearchDailyTransactionTypeEnum.itemName
          ? searchRequest.searchValue
          : null;
      searchRequest.itemBarcode =
        searchRequest.searchType == SearchDailyTransactionTypeEnum.barCode
          ? searchRequest.searchValue
          : null;
      searchRequest.itemCode =
        searchRequest.searchType == SearchDailyTransactionTypeEnum.code
          ? searchRequest.searchValue
          : null;
      searchRequest.code =
        searchRequest.searchType ==
        SearchDailyTransactionTypeEnum.transactionCode
          ? searchRequest.searchValue
          : null;
    }
    const result = await searchTransactions(searchRequest);
    setData(result?.Result || []);
    setTotalRows(result?.TotalRecoredCount || 0);
  };
  const onCurrentPageChange = async (pageNumber: number) => {
    if (pageNumber !== searchRequest.pageNumber) {
      const obj = { ...searchRequest };
      obj.pageNumber = pageNumber;
      setSearchRequest(obj);
    }
  };
  const onPageSizeChange = async (pageSize: number) => {
    if (pageSize !== searchRequest.pageSize) {
      const obj = { ...searchRequest };
      obj.pageSize = pageSize;
      setSearchRequest(obj);
    }
  };
  //#endregion
  //#region component
  const subHeaderComponent = useMemo(() => {
    return (
      <>
        <div className="row row-cols-1 row-cols-xxl-3 row-cols-xl-3 row-cols-lg-3 row-cols-md-2 row-cols-sm-1 g-md-4 g-sm-4 mb-1">
          <InputDatePicker
            selectedDate={searchRequest.fromDate}
            isClearable
            InputLabel={"from Date"}
            selectsEnd
            timeMode={InputDatePickerTimeDisplayModeEnum.TimeSelect}
            startDate={searchRequest.fromDate}
            className="form-control"
            // minDate={searchParams.fromDate}
            endDate={searchRequest.toDate}
            onChange={(date) => {
              setSearchRequest({
                ...searchRequest,
                fromDate: date === null ? undefined : date,
              });
            }}
          />
          <InputDatePicker
            selectedDate={searchRequest.toDate}
            isClearable
            InputLabel={"to Date"}
            selectsEnd
            timeMode={InputDatePickerTimeDisplayModeEnum.TimeSelect}
            startDate={searchRequest.fromDate}
            className="form-control"
            minDate={searchRequest.fromDate}
            endDate={searchRequest.toDate}
            onChange={(date) => {
              setSearchRequest({
                ...searchRequest,
                toDate: date === null ? undefined : date,
              });
            }}
          />

          <SelectBox
            labelName={
              _isSalesComponent
                ? getLabelName("Customer")
                : getLabelName("supplier")
            }
            isSingleSelect={true}
            selectedValues={
              searchRequest.customerId !== null &&
              searchRequest.customerId !== undefined
                ? [searchRequest.customerId.toString()]
                : null
            }
            source={beneficiaryList}
            onStatusChange={(e: LookupItemModel) => {
              setSearchRequest({
                ...searchRequest,
                customerId:
                  e === null || e.value === null ? null : Number(e.value),
              });
            }}
            multiselectRef={customerAccountsSelectBoxMultiselectRef}
          />
          <SelectBox
            labelName={getLabelName("Type")}
            isSingleSelect={true}
            selectedValues={[searchRequest.searchType.toString()] ?? null}
            source={searchTypeList}
            onStatusChange={(e: LookupItemModel) => {
              setSearchRequest({
                ...searchRequest,
                searchType: Number(e.value),
              });
            }}
          />
          <TextBox
            labelName={getLabelName("Value")}
            inputName={"filterTextBox"}
            inputValue={searchRequest.searchValue}
            onChange={(e: any) => {
              setSearchRequest({
                ...searchRequest,
                searchValue: e.target.value,
              });
            }}
          />
          <div className="col-md-4 d-flex justify-content-end">
            <PrivilegesChecker formId={formId} action="EnableSearch">
              <ButtonBox
                iconType="account-search"
                className="btn btn-primary btn-md mx-2 mb-2"
                onClick={() => {
                  setSearchRequest({
                    ...searchRequest,
                    searchGuid: generateGuid(),
                  });
                }}
              >
                {getLabelName("Search")}
              </ButtonBox>
            </PrivilegesChecker>

            <ButtonBox
              iconType="receipt"
              variant="danger"
              className="btn btn-danger btn-md mb-2"
              onClick={() => {
                customerAccountsSelectBoxMultiselectRef.current.clearValue();
                setSearchRequest({
                  ...initialValues,
                  searchGuid: generateGuid(),
                });
              }}
            >
              {getLabelName("Cancel")}
            </ButtonBox>
          </div>
        </div>
      </>
    );
  }, [searchRequest, beneficiaryList, cityList, searchTypeList]);
  //#endregion
  //#region html
  return (
    <>
      {loading && <LoadingBox />}
      {getPagePrivileges().EnableSearch && (
        <PrivilegesChecker formId={formId} action="EnableSearch">
          {subHeaderComponent}
        </PrivilegesChecker>
      )}
      <GridTable
        data={data}
        totalRows={totalRows}
        currentPage={searchRequest.pageNumber || 1}
        pageSize={searchRequest.pageSize || defaultPageSize}
        onCurrentPageChange={onCurrentPageChange}
        onPageSizeChange={onPageSizeChange}
        paginationType="server"
        columnsProps={{
          actions: availableActions, //  [],
          onActionEvent,
          isArabic,
        }}
        formId={formId}
        style={TableActionColumnsStyleEnum.Dropdown}
        actionResolver={actionResolver}
        useRowBasedActions={true}
        // subHeaderComponent={getPagePrivileges().search && subHeaderComponent}
      />
    </>
  );
  //#endregion
};
